import {
  filterValuesEqual,
  isFilterColumn,
  isFilterValueActive,
  parseStoredFilterValue,
  type FilterColumn,
  type FilterOption,
  type FilterState,
  type FilterValue,
} from "@/dashboard/inventory_management/common/portfolioTableFilters";

// The table's filters survive a refresh and a tab round-trip through one query param, written as
// `<scope>|<json>`. The JSON holds only the columns that differ from the page's defaults, by value
// (not by option index, so a refetch that adds an option doesn't shift anything). A defaulted
// column the user cleared is stored as `null`, so "every status ticked" can never fall back to the
// default after a reload. The scope stamp (the company ids) keeps one company's filters from being
// applied to another's data.

const SCOPE_SEPARATOR = "|";

/** What a param stores per column: a value valid for the column, or `null` for a cleared column. */
export type StoredFilterOverrides = Partial<
  Record<FilterColumn, FilterValue | null>
>;

/**
 * @param values Every column's value; the JSON lists the overrides in this object's key order.
 * @returns The param value, or `undefined` when every column is at its default so the param is
 *   dropped rather than left behind as a bare stamp.
 */
export const encodeTableFilterState = (
  values: FilterState,
  defaults: Partial<FilterState>,
  scope: string
): string | undefined => {
  const overrides: StoredFilterOverrides = {};
  for (const column of Object.keys(values).filter(isFilterColumn)) {
    const value = values[column];
    if (filterValuesEqual(column, value, defaults[column])) continue;
    overrides[column] = isFilterValueActive(column, value) ? value : null;
  }
  return Object.keys(overrides).length > 0
    ? `${scope}${SCOPE_SEPARATOR}${JSON.stringify(overrides)}`
    : undefined;
};

/**
 * Reads the param back. The stored JSON is untrusted: only filterable columns are kept, each only
 * when its value fits the column's kind, so anything else falls back to the default.
 *
 * @returns The stored overrides, or `null` when the value is absent, malformed, or stamped for a
 *   different scope (including the chart's old index-based format) — callers then use defaults.
 */
export const decodeTableFilterState = (
  raw: string | undefined,
  scope: string
): StoredFilterOverrides | null => {
  if (!raw) return null;
  const separator = raw.indexOf(SCOPE_SEPARATOR);
  if (separator === -1 || raw.slice(0, separator) !== scope) return null;
  try {
    const parsed: unknown = JSON.parse(raw.slice(separator + 1));
    return typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? parseOverrides(parsed)
      : null;
  } catch {
    return null;
  }
};

/**
 * Fits one stored value to the column as it is today: list values that are no longer among its
 * options are dropped, and a list left empty falls back to the column's default. A list value
 * matches its option by string form and restores as the option's own value, so a hand-edited
 * `"true"` still filters as `true`.
 *
 * @returns The value to apply, `null` to clear the column, or `undefined` to keep its default.
 */
export const restoreStoredFilterValue = (
  stored: FilterValue | null,
  options: FilterOption[] | undefined
): FilterValue | null | undefined => {
  if (!Array.isArray(stored)) return stored;
  const known = options
    ? stored.flatMap((value) => {
        const option = options.find(
          (candidate) => String(candidate.value) === String(value)
        );
        return option ? [option.value] : [];
      })
    : stored;
  return known.length > 0 ? known : undefined;
};

const parseOverrides = (stored: object): StoredFilterOverrides => {
  const overrides: StoredFilterOverrides = {};
  const entries: [string, unknown][] = Object.entries(stored);
  for (const [column, raw] of entries) {
    if (!isFilterColumn(column)) continue;
    const value = raw === null ? null : parseStoredFilterValue(column, raw);
    if (value !== undefined) overrides[column] = value;
  }
  return overrides;
};
